import { message } from 'antd';
import { filterDeep } from 'deepdash-es/standalone';
import { put, select, takeLatest } from 'redux-saga/effects';
import { StoreActionProps } from 'data/core';
import {
  DynamicProjectCustomField,
  DynamicProjectFields,
  ProjectTemplatePreview,
  TemplatesAPI,
} from 'data/templates/api';
import { logger } from 'services';
import {
  GetDynamicTemplatePreviewActionPayload,
  UpdateDynamicTemplateConditionsActionPayload,
  getDynamicTemplatePreviewAction,
  updateDynamicTemplateConditionsAction,
} from '../actions';
import {
  denormalizePayload,
  NormalizedProjectTemplatePayload,
  setDynamicConditionInPayload,
  validatePhaseShiftInPayload,
} from '../api/transform';
import { updateDynamicConditions } from '../slices';
import { ProjectTemplateData } from '../types';
import { findLargestSumOfStartDayDuration, findLowestStartDay } from '../utils';
import { getActiveProjectTemplate } from './save';

function* updateDynamicTemplateConditionsSaga(
  action: StoreActionProps<UpdateDynamicTemplateConditionsActionPayload>,
) {
  try {
    const {
      payload: { entityId, conditions },
    } = action;

    yield put(updateDynamicConditions({ entityId, conditions }));
  } catch (error) {
    logger.error('an error on updating dynamic conditions', { error });
    message.error('There was an error on updating dynamic conditions');
  }
}

function* getDynamicTemplatePreviewSaga(
  action: StoreActionProps<GetDynamicTemplatePreviewActionPayload>,
) {
  const {
    payload: { fields, compressTimeline, onSuccess, onFinally },
  } = action;

  const projectTemplate: NormalizedProjectTemplatePayload = yield select(
    getActiveProjectTemplate,
  );

  try {
    const _template = setDynamicConditionInPayload(projectTemplate);

    const _phaseShiftValidTemplate = validatePhaseShiftInPayload(_template);

    const template = denormalizePayload(_phaseShiftValidTemplate);

    template.templateContent.projectPhases =
      filterDeep(
        template.templateContent.projectPhases,
        (object) => {
          if (!object) {
            return false;
          }
          if (object.isNew) {
            return false;
          }

          delete object.isNew;

          return true;
        },
        { childrenPath: ['tasks', 'subTasks'] },
      ) || [];

    const largestDuration = findLargestSumOfStartDayDuration(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );
    const lowestStartDay = findLowestStartDay(
      template.templateContent
        .projectPhases as unknown[] as ProjectTemplateData[],
    );

    const total = largestDuration - lowestStartDay;

    template.templateContent.duration = total;
    template.templateContent.startDay = lowestStartDay;

    const project: DynamicProjectFields = {
      fields: [] as unknown as DynamicProjectCustomField[],
    };

    const validFieldValues = [0, false];

    Object.keys(fields).forEach((fieldId) => {
      const fieldValue = fields[fieldId];

      if (!fieldValue && !validFieldValues.includes(fieldValue)) {
        return;
      }

      if (Number.isNaN(parseInt(fieldId, 10))) {
        project[fieldId === 'arr' ? 'annualizedRecurringRevenue' : fieldId] =
          fieldValue;
      } else {
        project.fields?.push({
          fieldId,
          fieldValue: Array.isArray(fieldValue)
            ? fieldValue.map((_field) => _field.value ?? _field)
            : fieldValue.value ?? fieldValue,
        });
      }
    });

    const requestPayload: ProjectTemplatePreview = {
      templateRequest: {
        templateContent: template.templateContent,
        objectType: template.objectType,
        compressTimeline,
      },
      project,
    };

    const templatePreview: ProjectTemplatePreview =
      yield TemplatesAPI.preview<ProjectTemplatePreview>(requestPayload);

    if (onSuccess) {
      onSuccess(templatePreview);
    }
  } catch (error) {
    logger.error('an error while generating preview for dynamic template', {
      error,
    });

    message.error(
      'There was an error while generating preview for dynamic template',
    );
  } finally {
    if (onFinally) {
      onFinally();
    }
  }
}

export function* watchUpdateDynamicTemplateConditionsSaga(): Generator {
  yield takeLatest(
    updateDynamicTemplateConditionsAction.type,
    updateDynamicTemplateConditionsSaga,
  );
}

export function* watchGetDynamicTemplatePreviewSaga(): Generator {
  yield takeLatest(
    getDynamicTemplatePreviewAction.type,
    getDynamicTemplatePreviewSaga,
  );
}
